import type { BillingCustomer, BillingTransaction } from '@common/types/billing';
import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import BillingSummary from '@/components/BillingSummary';
import BillingSummarySkeleton from '@/components/BillingSummary/BillingSummarySkeleton';
import { getBillingCustomer, getProspectBilling } from '@/services/BilllingService';
import { formatBillingDate } from '@/utils/format';
import { buildInvoiceCustomer } from '@/utils/invoices/customer';
import ProspectDetailsErrorPage from './ProspectDetailsErrorPage';

interface Props {
    prospectId?: string;
    lawFirmId: number;
    lawFirmCompanyName?: string;
    prospectName: string;
    // Typed as a Date in the response, but a string once it's been through JSON
    completedAt: Date | string | null;
}

export default function BillingTab({
    prospectId,
    lawFirmId,
    lawFirmCompanyName,
    prospectName,
    completedAt,
}: Props) {
    const {
        data: transactions = [],
        isPending: isTransactionsPending,
        isError: isTransactionsError,
        isFetching: isTransactionsFetching,
        refetch: refetchTransactions,
    } = useQuery<BillingTransaction[]>({
        queryKey: ['prospect-billing', prospectId],
        queryFn: async () => {
            if (!prospectId) return [];
            const response = await getProspectBilling(prospectId);
            return response.data ?? [];
        },
        enabled: !!prospectId,
    });

    const {
        data: billingCustomer,
        isPending: isCustomerPending,
        isError: isCustomerError,
        isFetching: isCustomerFetching,
        refetch: refetchCustomer,
    } = useQuery<BillingCustomer | null>({
        queryKey: ['billing-customer', lawFirmId],
        queryFn: async () => {
            if (!lawFirmId) return null;
            const response = await getBillingCustomer(lawFirmId);
            return response.data ?? null;
        },
        enabled: !!lawFirmId,
    });

    const invoiceCustomer = useMemo(
        () => buildInvoiceCustomer(billingCustomer, lawFirmCompanyName),
        [billingCustomer, lawFirmCompanyName],
    );

    const handleRetry = () => {
        void refetchTransactions();
        void refetchCustomer();
    };

    if (isTransactionsPending || isCustomerPending) {
        return (
            <Box sx={{ p: 3 }}>
                <BillingSummarySkeleton />
            </Box>
        );
    }

    if (isTransactionsError || isCustomerError) {
        return (
            <ProspectDetailsErrorPage
                message="There was an error fetching billing information"
                onRetry={handleRetry}
                isRetrying={isTransactionsFetching || isCustomerFetching}
            />
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <BillingSummary
                transactions={transactions}
                dateRangeLabel={prospectName}
                invoicePayload={{
                    customer: invoiceCustomer,
                    invoice: {
                        number: `${lawFirmId}-${prospectName}`,
                        date: formatBillingDate(completedAt),
                    },
                }}
            />
        </Box>
    );
}
